import React, { useState } from "react";
import styles from "../Transcript.module.css";
import useFlexibleData from "../../../../hooks/useFlexibleData";
import useTransferStudent from "../../../../hooks/useTransferStudent";

export default function HavenTranscript({
  keyArray,
  gradeRows,
  attendanceRows,
  settingData,
  displaySettings = {},
}) {
  const [documentNo, setDocumentNo] = useState("");

  const {
    flexibleDataQuery: { data: flexibleData },
  } = useFlexibleData({
    schoolid: keyArray[0]?.schoolid,
    main_category: "transferStudent",
    sub_category: keyArray[0]?.username,
  });

  const {
    transferStudentQuery: { data: transferStudentData },
  } = useTransferStudent({
    schoolid: keyArray[0]?.schoolid,
    username: keyArray[0]?.username,
  });

  // 마지막 visible 학기 찾기 (Cumulative GPA 표시용)
  const findLastVisibleSemester = () => {
    const semesters = [
      { grade: "9", term: "1" },
      { grade: "9", term: "2" },
      { grade: "10", term: "1" },
      { grade: "10", term: "2" },
      { grade: "11", term: "1" },
      { grade: "11", term: "2" },
      { grade: "12", term: "1" },
      { grade: "12", term: "2" },
    ];

    let lastVisibleSemester = null;

    // 역순으로 순회하여 마지막 visible 학기 찾기
    for (let i = semesters.length - 1; i >= 0; i--) {
      const { grade, term } = semesters[i];
      const semesterKey = `${grade}-${term}`;
      const settings = displaySettings[semesterKey] || {
        visible: true,
        courseTitleOnly: false,
      };

      // 정확히 visible이 false로 체크되어 있을 때만 제외
      if (settings.visible === false) {
        continue;
      }

      // visible이 true이면 (courseTitleOnly여도 상관없이) 마지막 visible 학기로 간주
      lastVisibleSemester = { grade, term };
      break;
    }

    return lastVisibleSemester;
  };

  const lastVisibleSemester = findLastVisibleSemester();

  // 총 GPA 계산
  const { totalGPA } = calculateTotalGPA(
    gradeRows,
    flexibleData,
    transferStudentData,
    displaySettings
  );

  const formatBirthday = (dateString) => {
    // 데이터가 없는 경우 처리
    if (!dateString) return "";

    try {
      // 날짜 형식 검증 (YYYY-MM-DD 형식인지)
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        console.warn("Invalid date format:", dateString);
        return dateString; // 원본 데이터 반환
      }

      const date = new Date(dateString);

      // 유효하지 않은 날짜인 경우 (예: 2023-13-45)
      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", dateString);
        return dateString; // 원본 데이터 반환
      }

      const formatted = date
        .toLocaleDateString("en-US", {
          month: "long",
          day: "2-digit",
          year: "numeric",
        })
        .replace(/,/g, "")
        .replace(/ /g, "/");

      return formatted;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // 에러 발생 시 원본 데이터 반환
    }
  };

  const transcriptHeader = () => {
    // console.log("flexibleData", flexibleData);
    // console.log("transferStudentData", transferStudentData);
    return (
      <div className={styles.headerBar}>
        <div className={styles.headerLeft}>
          <div className={styles.studentInfo}>
            <div className={styles.printRow}>
              <div style={{ flex: "0 0 40%" }}>■ Document No.</div>
              <div style={{ flex: "0 0 58%" }}>
                :{" "}
                <input
                  type="text"
                  className={styles.printInput}
                  value={documentNo}
                  onChange={(e) => setDocumentNo(e.target.value)}
                  style={{ width: "95%" }}
                />
              </div>
              <div style={{ flex: "0 0 40%" }}>■ Student ID</div>
              <div style={{ flex: "0 0 58%" }}>
                :{" "}
                {keyArray[0]?.idatschool || keyArray[0]?.username.split("@")[0]}
              </div>
              <div style={{ flex: "0 0 40%" }}>■ Student Name</div>
              <div style={{ flex: "0 0 58%" }}>: {keyArray[0]?.fullname}</div>
              {/* <div style={{ flex: "0 0 40%" }}>■ Grade</div>
              <div style={{ flex: "0 0 58%" }}>: {keyArray[0]?.[2]}th</div> */}
              <div style={{ flex: "0 0 40%" }}>■ Date of Birth</div>
              <div style={{ flex: "0 0 58%" }}>
                : {formatBirthday(keyArray[0]?.birthday)}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.headerCenter}>
          <h2
            style={{
              fontFamily: "Times New Roman",
              textShadow: "3px 3px 0px lightgray",
            }}
          >
            TRANSCRIPT
          </h2>
          <div className="d-flex">
            <div style={{ flex: "0 0 50%", borderRight: "1px solid black" }}>
              <div style={{ backgroundColor: "#CCCCCC" }}>Grading System</div>
              <div className="d-flex align-items-center">
                <div className={styles.systemBox}>
                  <div style={{ flex: "0 0 25%" }}>A+</div>
                  <div style={{ flex: "0 0 75%" }}>97-above</div>
                  <div style={{ flex: "0 0 25%" }}>A</div>
                  <div style={{ flex: "0 0 75%" }}>93-96</div>
                  <div style={{ flex: "0 0 25%" }}>A-</div>
                  <div style={{ flex: "0 0 75%" }}>90-92</div>
                  <div style={{ flex: "0 0 25%" }}>B+</div>
                  <div style={{ flex: "0 0 75%" }}>87-89</div>
                  <div style={{ flex: "0 0 25%" }}>B</div>
                  <div style={{ flex: "0 0 75%" }}>83-86</div>
                  <div style={{ flex: "0 0 25%" }}>B-</div>
                  <div style={{ flex: "0 0 75%" }}>80-82</div>
                </div>
                <div className={styles.systemBox}>
                  <div style={{ flex: "0 0 25%" }}>C+</div>
                  <div style={{ flex: "0 0 75%" }}>77-79</div>
                  <div style={{ flex: "0 0 25%" }}>C</div>
                  <div style={{ flex: "0 0 75%" }}>73-76</div>
                  <div style={{ flex: "0 0 25%" }}>C-</div>
                  <div style={{ flex: "0 0 75%" }}>70-72</div>
                  <div style={{ flex: "0 0 25%" }}>D+</div>
                  <div style={{ flex: "0 0 75%" }}>67-69</div>
                  <div style={{ flex: "0 0 25%" }}>D</div>
                  <div style={{ flex: "0 0 75%" }}>60-66</div>
                  <div style={{ flex: "0 0 25%" }}>F</div>
                  <div style={{ flex: "0 0 75%" }}>59-below</div>
                </div>
                <div className={styles.systemBox}>
                  <div style={{ flex: "0 0 25%" }}>I</div>
                  <div
                    style={{
                      flex: "0 0 75%",
                      letterSpacing: "-0.4px",
                    }}
                  >
                    Incomplete
                  </div>
                  <div style={{ flex: "0 0 25%" }}>W</div>
                  <div style={{ flex: "0 0 75%", letterSpacing: "-0.4px" }}>
                    Withdrawn
                  </div>
                  <div style={{ flex: "0 0 25%" }}>P</div>
                  <div style={{ flex: "0 0 75%" }}>Pass</div>
                  <div style={{ flex: "0 0 25%" }}>F</div>
                  <div style={{ flex: "0 0 75%" }}>Fail</div>
                  <div style={{ flex: "0 0 25%" }}>AU</div>
                  <div style={{ flex: "0 0 75%" }}>Audit</div>
                </div>
              </div>
            </div>
            <div style={{ borderRight: "1px solid black" }}>
              <div style={{ backgroundColor: "#CCCCCC" }}>Effort System</div>
              <div
                className="d-flex align-items-center"
                style={{ height: "84%" }}
              >
                <div
                  className={styles.systemBox}
                  style={{ paddingLeft: "0px" }}
                >
                  <div style={{ flex: "0 0 25%" }}>1</div>
                  <div style={{ flex: "0 0 75%" }}>Outstanding</div>
                  <div style={{ flex: "0 0 25%" }}>2</div>
                  <div style={{ flex: "0 0 75%" }}>Above average</div>
                  <div style={{ flex: "0 0 25%" }}>3</div>
                  <div style={{ flex: "0 0 75%" }}>Average</div>
                  <div style={{ flex: "0 0 25%" }}>4</div>
                  <div style={{ flex: "0 0 75%" }}>Inconsistent</div>
                  <div style={{ flex: "0 0 25%" }}>5</div>
                  <div style={{ flex: "0 0 75%" }}>Poor</div>
                </div>
              </div>
            </div>
            <div style={{ minWidth: "166px" }}>
              <div style={{ backgroundColor: "#CCCCCC" }}>Attendance</div>
              <div
                className="d-flex align-items-center"
                style={{ height: "84%" }}
              >
                <div className={styles.systemBox} style={{ width: "50%" }}>
                  <div style={{ flex: "0 0 25%" }}>P</div>
                  <div style={{ flex: "0 0 75%" }}>Present</div>
                  <div style={{ flex: "0 0 25%" }}>A</div>
                  <div style={{ flex: "0 0 75%" }}>Absent</div>
                  <div style={{ flex: "0 0 25%" }}>T</div>
                  <div style={{ flex: "0 0 75%" }}>Tardy</div>
                  <div style={{ flex: "0 0 25%" }}>E</div>
                  <div style={{ flex: "0 0 75%" }}>Early Leave</div>
                </div>
                <div className={styles.systemBox} style={{ width: "50%" }}>
                  <div style={{ flex: "0 0 25%" }}>E</div>
                  <div style={{ flex: "0 0 75%" }}>Excused</div>
                  <div style={{ flex: "0 0 25%" }}>S</div>
                  <div style={{ flex: "0 0 75%" }}>Sick</div>
                  <div style={{ flex: "0 0 25%" }}>U</div>
                  <div style={{ flex: "0 0 75%" }}>Unexcused</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.headerRight}>
          <div>
            <div className="d-flex align-items-center mb-2">
              <div className="text-center" style={{ minWidth: "100px" }}>
                <img
                  src="https://res.cloudinary.com/dc9kbbxxe/image/upload/v1743326351/eduseed/haven/%ED%97%A4%EC%9D%B4%EB%B8%90_%EB%A1%9C%EA%B3%A01_b7ftjz.png"
                  alt="Haven Christian School"
                  width="auto"
                  height="60px"
                />
              </div>
              <div className="text-start ms-2">
                <h5 style={{ fontSize: "17px" }} className="mb-0">
                  HAVEN <br />
                  CHRISTIAN
                  <br />
                  SCHOOL
                </h5>
              </div>
            </div>

            <div className="text-start">
              <div className="d-flex">
                <div style={{ flex: "0 0 60px", textAlign: "right" }}>
                  Address
                </div>
                <div style={{ flex: "0 0 20px", textAlign: "center" }}>:</div>
                <div style={{ flex: "1" }}>
                  32, Yangjimaeul 4-ro, Gwacheon-si
                </div>
              </div>
              <div className="d-flex">
                <div style={{ flex: "0 0 60px" }}></div>
                <div style={{ flex: "0 0 20px" }}></div>
                <div style={{ flex: "1" }}>Gyeonggi-do, South Korea</div>
              </div>
              <div className="d-flex">
                <div style={{ flex: "0 0 60px", textAlign: "right" }}>
                  Phone
                </div>
                <div style={{ flex: "0 0 20px", textAlign: "center" }}>:</div>
                <div style={{ flex: "1" }}>+82-2-503-0527</div>
              </div>
              <div className="d-flex">
                <div style={{ flex: "0 0 60px", textAlign: "right" }}>
                  e-mail
                </div>
                <div style={{ flex: "0 0 20px", textAlign: "center" }}>:</div>
                <div style={{ flex: "1" }}>office@haven.or.kr</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  function gradeTableByTerm(term, studentYear, gradeData, attendanceData) {
    // displaySettings 확인
    const semesterKey = `${studentYear}-${term}`;
    const settings = displaySettings[semesterKey] || {
      visible: true,
      courseTitleOnly: false,
    };

    // visible이 false면 출력하지 않음
    if (settings.visible === false) {
      return null;
    }

    // 마지막 visible 학기인지 확인
    const isLastVisibleSemester =
      lastVisibleSemester &&
      lastVisibleSemester.grade === studentYear &&
      lastVisibleSemester.term === term;

    // 해당 학기/학년의 데이터만 필터링

    // 이 부분은 원조 gradeTableByTerm 안에 넣자.
    const selectedFlexibleData = flexibleData?.filter(
      (r) =>
        r.data_value?.studentYear.toString() === studentYear.toString() &&
        r.data_value?.termName.toString() === term.toString()
    );

    if (selectedFlexibleData?.length > 1) {
      alert(
        "해당 학년도 학기에 해당하는 데이터가 여러 개가 있습니다. 관리자에게 문의 해 주세요. 우선 제일 처음 있는 데이터를 사용합니다."
      );
    }

    const gradeDataExisting = gradeData?.filter(
      (item) =>
        item.lesson?.TermDates?.term_name === term &&
        item.studentYear === studentYear
    );

    if (gradeDataExisting.length < 1 && selectedFlexibleData?.length > 0) {
      const exisitingTransferData = selectedFlexibleData[0];
      const schoolYear = settingData.find(
        (item) =>
          item.id.toString() ===
          exisitingTransferData?.school_year_id.toString()
      )?.school_year;

      return specialGradeTableByTerm(
        term,
        studentYear,
        transferStudentData,
        gradeRows,
        exisitingTransferData,
        schoolYear
      );
    }

    // AP 과목 존재 여부 확인
    // const apIn11Or12 = gradeData.some(
    //   (item) =>
    //     item.lesson_management?.ap?.applied === true &&
    //     (item.studentYear === "11" || item.studentYear === "12")
    // );

    // const apIn9Or10 = gradeData.some(
    //   (item) =>
    //     item.lesson_management?.ap?.applied === true &&
    //     (item.studentYear === "9" || item.studentYear === "10")
    // );

    const retakeSeniorIn11Or12 = gradeData.some(
      (item) =>
        (item.lesson_management?.retake_senior?.type === "retake" ||
          item.lesson_management?.retake_senior?.type === "senior") &&
        (item.studentYear === "11" || item.studentYear === "12")
    );

    const retakeSeniorIn9Or10 = gradeData.some(
      (item) =>
        (item.lesson_management?.retake_senior?.type === "retake" ||
          item.lesson_management?.retake_senior?.type === "senior") &&
        (item.studentYear === "9" || item.studentYear === "10")
    );

    // AP 과목 처리: applied가 true인 경우 매칭되는 item의 studentYear와 TermDates를 가져옴
    gradeData.forEach((item) => {
      const ap = item?.lesson_management?.ap;
      const retakeSenior = item?.lesson_management?.retake_senior;

      if (ap?.applied === true) {
        // 매칭되는 item 찾기
        const matchingItem = gradeData.find((otherItem) => {
          return (
            otherItem !== item && // 자기 자신은 제외
            ap?.school_year_id?.toString() ===
              otherItem.school_year_id?.toString() &&
            ap?.termName?.toString() ===
              otherItem.lesson?.TermDates?.term_name?.toString()
          );
        });

        if (matchingItem) {
          // 찾은 item의 studentYear와 lesson.TermDates로 교체
          item.studentYear = matchingItem.studentYear;
          item.lesson.TermDates = { ...matchingItem.lesson.TermDates };
        }
      }

      if (retakeSenior?.type?.length > 0) {
        item.grade = retakeSenior.grade;
        item.convert100 = retakeSenior.convert100;
        item.subjectName = retakeSenior.subject_name;
        item.lesson.subject_name = retakeSenior.subject_name;
      }
    });

    const filteredData = gradeData
      ?.filter(
        (item) =>
          item.lesson?.TermDates?.term_name === term &&
          item.studentYear === studentYear
      )
      .sort((a, b) => {
        // 1. subjectOrder가 있는 것이 우선
        const hasOrderA = a.extraAttributes?.subjectOrder ? 1 : 0;
        const hasOrderB = b.extraAttributes?.subjectOrder ? 1 : 0;

        if (hasOrderA !== hasOrderB) {
          return hasOrderB - hasOrderA; // subjectOrder가 있는 것이 먼저
        }

        // 2. 둘 다 subjectOrder가 있으면 그 순서대로
        if (hasOrderA && hasOrderB) {
          return (
            Number(a.extraAttributes.subjectOrder) -
            Number(b.extraAttributes.subjectOrder)
          );
        }

        // 3. 둘 다 subjectOrder가 없으면 과목명으로
        return a.subjectName.localeCompare(b.subjectName);
      });

    // 학기의 시작일과 종료일 가져오기 (filteredData를 비우기 전에 미리 저장)
    const termStartDate = filteredData[0]?.lesson?.TermDates?.start_date;
    const termEndDate = filteredData[0]?.lesson?.TermDates?.end_date;

    // 학기가 종료되었는지 체크
    let isTermEnded = true;
    const isCourseTitleOnly = settings.courseTitleOnly === true;

    if (termEndDate) {
      const currentDate = new Date();
      const endDate = new Date(termEndDate);

      // 종료일이 현재 날짜보다 이후면 학기가 아직 진행 중
      if (endDate > currentDate) {
        isTermEnded = false;
        // course title only가 아닌 경우에만 데이터를 빈 배열로 교체
        // course title only인 경우에는 course title을 표시해야 하므로 데이터 유지
        if (!isCourseTitleOnly) {
          filteredData.splice(0, filteredData.length);
        }
      }
    }

    // course title only 처리
    let displayData = filteredData;

    // 출석 데이터 계산
    const attendanceStats = attendanceStatic(
      attendanceData,
      termStartDate,
      termEndDate
    );

    // 학기 이름과 학년도 포맷팅
    const formatTermHeader = (term, studentYear) => {
      const termName = term === "1" ? "Spring" : "Fall";
      const schoolYear = filteredData[0]?.school_year || "";
      return `(${termName}) School Year ${schoolYear} Grade : ${studentYear}th`;
    };
    // GPA 계산 (courseTitleOnly가 true면 GPA 계산에서 제외)
    const { totalCredits, gradeAverage } = isCourseTitleOnly
      ? { totalCredits: "0.00", gradeAverage: "0.0" }
      : calculateGPA(filteredData);

    return (
      <div className={styles.semesterBlock}>
        <div className={styles.semesterHeader}>
          {formatTermHeader(term, studentYear)}
        </div>
        <table
          style={{
            maxHeight: "250px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "38px", padding: "0px" }}>GR</th>
              <th>COURSE TITLE</th>
              <th style={{ width: "40px", padding: "0px" }}>UNITS</th>
              <th style={{ width: "30px", padding: "0px" }}>GD</th>
              <th style={{ width: "30px", padding: "0px" }}>SC</th>
              <th style={{ width: "30px", padding: "0px" }}>EF</th>
              <th style={{ width: "76px", padding: "0px" }} colSpan="2">
                ATTENDANCE
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={{
                overflowY: "auto",
                ...(filteredData.length > 15
                  ? { lineHeight: "0.95" }
                  : filteredData.length > 12
                  ? { lineHeight: "1.2" }
                  : {}),
                height: "211px",
              }}
            >
              <td className="text-center">CORE</td>
              <td className="text-start align-top">
                {displayData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.subjectName}
                    {item.lesson_management?.retake_senior?.type?.length > 0
                      ? "*"
                      : ""}
                    <br />
                  </React.Fragment>
                ))}
              </td>
              <td className="align-top">
                {isCourseTitleOnly
                  ? // Course Title Only일 때는 빈 셀
                    displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        <br />
                      </React.Fragment>
                    ))
                  : displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        {getLessonUnit(item)}
                        <br />
                      </React.Fragment>
                    ))}
              </td>
              <td className="align-top">
                {isCourseTitleOnly
                  ? // Course Title Only일 때는 빈 셀
                    displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        <br />
                      </React.Fragment>
                    ))
                  : displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.extra_data?.academicStatus
                          ? item.extra_data?.academicStatus === "withdrawn"
                            ? "W"
                            : item.extra_data?.academicStatus === "incomplete"
                            ? "I"
                            : item.convert100 || ""
                          : item.convert100 || ""}
                        <br />
                      </React.Fragment>
                    ))}
              </td>
              <td className="align-top">
                {isCourseTitleOnly
                  ? // Course Title Only일 때는 빈 셀
                    displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        <br />
                      </React.Fragment>
                    ))
                  : displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.extra_data?.academicStatus === "withdrawn" ||
                        item.extra_data?.academicStatus === "incomplete" ||
                        isNaN(item.grade)
                          ? "-"
                          : Math.round(Number(item.grade))}
                        <br />
                      </React.Fragment>
                    ))}
              </td>
              <td className="align-top">
                {isCourseTitleOnly
                  ? // Course Title Only일 때는 빈 셀
                    displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        <br />
                      </React.Fragment>
                    ))
                  : displayData.map((item, index) => (
                      <React.Fragment key={index}>
                        {getEffort(item)}
                        <br />
                      </React.Fragment>
                    ))}
              </td>
              <td
                className="align-top"
                style={{ width: "38px", padding: "0px", lineHeight: "1.5" }}
              >
                P <br />
                SA <br />
                UA <br />
                EA <br />
                ST <br />
                UT <br />
                ET <br />
                SE <br />
                UE <br />
                EE
              </td>
              <td
                className="align-top"
                style={{ width: "38px", padding: "0px", lineHeight: "1.5" }}
              >
                {isCourseTitleOnly || !isTermEnded ? (
                  ""
                ) : (
                  <>
                    {attendanceStats.attendedDays} <br />
                    {attendanceStats.sickAbsence} <br />
                    {attendanceStats.justAbsence} <br />
                    {attendanceStats.etcAbsence} <br />
                    {attendanceStats.sickTardiness} <br />
                    {attendanceStats.justTardiness} <br />
                    {attendanceStats.etcTardiness} <br />
                    {attendanceStats.sickLeave} <br />
                    {attendanceStats.justLeave} <br />
                    {attendanceStats.etcLeave}
                  </>
                )}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td className="text-center p-0" colSpan="8">
                <span className="ms-5"></span>
                Units Earned : {isCourseTitleOnly ? "0.00" : totalCredits}{" "}
                <span className="me-5"></span>
                Cumulative Average : {isCourseTitleOnly ? "0.0" : gradeAverage}
                <span className="me-4"></span>
                {!isCourseTitleOnly &&
                displayData.length > 0 &&
                gradeAverage !== "0.0"
                  ? getLetterGrade(parseFloat(gradeAverage))
                  : ""}
                <span className="me-3"></span>
              </td>
            </tr>
            {isLastVisibleSemester && (
              <tr>
                <td
                  colSpan="8"
                  className="text-end p-0 pe-3 fw-bold"
                  style={{
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "none",
                  }}
                >
                  <span style={{ marginLeft: "163px" }}></span>
                  Cumulative GPA : {totalGPA}
                </td>
              </tr>
            )}

            {((studentYear === "9" && retakeSeniorIn9Or10) ||
              (studentYear === "11" && retakeSeniorIn11Or12)) &&
              term === "1" && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-start p-0 ps-2"
                    style={{
                      borderLeft: "none",
                      borderRight: "none",
                      borderBottom: "none",
                    }}
                  >
                    Asterisk(*) at the end of course title denotes Retake/Senior
                    Packet
                  </td>
                </tr>
              )}
          </tfoot>
        </table>
        {term === "1" && (
          <div>
            <span></span>
          </div>
        )}
      </div>
    );
  }

  // 학생 데이터 유효성 검증
  if (!keyArray || !Array.isArray(keyArray) || keyArray.length === 0) {
    return (
      <div className="text-center p-4">
        <h4>No student selected</h4>
        <p>Please select a student to view their transcript.</p>
      </div>
    );
  }

  // 모든 학기 정의 (순서대로)
  const allSemesters = [
    { term: "1", grade: "9" }, // Spring 9th
    { term: "2", grade: "9" }, // Fall 9th
    { term: "1", grade: "10" }, // Spring 10th
    { term: "2", grade: "10" }, // Fall 10th
    { term: "1", grade: "11" }, // Spring 11th
    { term: "2", grade: "11" }, // Fall 11th
    { term: "1", grade: "12" }, // Spring 12th
    { term: "2", grade: "12" }, // Fall 12th
  ];

  // visible인 학기만 필터링
  const visibleSemesters = allSemesters
    .map((sem) => {
      const component = gradeTableByTerm(
        sem.term,
        sem.grade,
        gradeRows,
        attendanceRows
      );
      return component ? { ...sem, component } : null;
    })
    .filter((item) => item !== null);

  // 페이지당 4개씩 배치 (순서: 위의 왼쪽, 아래의 왼쪽, 위의 오른쪽, 아래의 오른쪽)
  const gridPositions = [
    { gridRow: 1, gridCol: 1 }, // 위의 왼쪽
    { gridRow: 2, gridCol: 1 }, // 아래의 왼쪽
    { gridRow: 1, gridCol: 2 }, // 위의 오른쪽
    { gridRow: 2, gridCol: 2 }, // 아래의 오른쪽
  ];

  // 첫 페이지와 두 번째 페이지로 나누기
  const firstPageItems = visibleSemesters.slice(0, 4);
  const secondPageItems = visibleSemesters.slice(4, 8);

  return (
    <>
      {firstPageItems.length > 0 && (
        <div className={styles.firstPage}>
          {transcriptHeader()}
          <div className={styles.gradesContainer}>
            {firstPageItems.map((sem, index) => (
              <div
                key={`${sem.grade}-${sem.term}`}
                style={{
                  gridRow: gridPositions[index].gridRow,
                  gridColumn: gridPositions[index].gridCol,
                }}
              >
                {sem.component}
              </div>
            ))}
          </div>
          <div
            className={styles.pageBottomLine}
            style={{
              height: "2px",
              backgroundColor: "black",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
            }}
          ></div>
        </div>
      )}
      {secondPageItems.length > 0 && (
        <div className={styles.secondPage}>
          {transcriptHeader()}
          <div className={styles.gradesContainer}>
            {secondPageItems.map((sem, index) => (
              <div
                key={`${sem.grade}-${sem.term}`}
                style={{
                  gridRow: gridPositions[index].gridRow,
                  gridColumn: gridPositions[index].gridCol,
                }}
              >
                {sem.component}
              </div>
            ))}
          </div>
          <div
            className={styles.pageBottomLine}
            style={{
              height: "2px",
              backgroundColor: "black",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
            }}
          ></div>
        </div>
      )}
    </>
  );
}

function attendanceStatic(attendanceData, start_date, end_date) {
  if (!attendanceData || !start_date || !end_date) {
    return {
      attendedDays: 0,
      sickAbsence: 0,
      justAbsence: 0,
      etcAbsence: 0,
      sickTardiness: 0,
      justTardiness: 0,
      etcTardiness: 0,
      sickLeave: 0,
      justLeave: 0,
      etcLeave: 0,
      sickNoclass: 0,
      justNoclass: 0,
      etcNoclass: 0,
    };
  }

  const filteredData = attendanceData.filter(
    (r) => r.date >= start_date && r.date <= end_date
  );

  const attendedDays = filteredData.filter(
    (r) =>
      r.attendance_status !== "질결" &&
      r.attendance_status !== "무결" &&
      r.attendance_status !== "기결" &&
      r.attendance_status !== ""
  ).length;

  const sickAbsence = filteredData.filter(
    (r) => r.attendance_status === "질결"
  ).length;
  const justAbsence = filteredData.filter(
    (r) => r.attendance_status === "무결"
  ).length;
  const etcAbsence = filteredData.filter(
    (r) => r.attendance_status === "기결"
  ).length;
  const sickTardiness = filteredData.filter(
    (r) => r.attendance_status === "질지"
  ).length;
  const justTardiness = filteredData.filter(
    (r) => r.attendance_status === "무지"
  ).length;
  const etcTardiness = filteredData.filter(
    (r) => r.attendance_status === "기지"
  ).length;
  const sickLeave = filteredData.filter(
    (r) => r.attendance_status === "질조"
  ).length;
  const justLeave = filteredData.filter(
    (r) => r.attendance_status === "무조"
  ).length;
  const etcLeave = filteredData.filter(
    (r) => r.attendance_status === "기조"
  ).length;
  const sickNoclass = filteredData.filter(
    (r) => r.attendance_status === "질결과"
  ).length;
  const justNoclass = filteredData.filter(
    (r) => r.attendance_status === "무결과"
  ).length;
  const etcNoclass = filteredData.filter(
    (r) => r.attendance_status === "기결과"
  ).length;

  return {
    attendedDays,
    sickAbsence,
    justAbsence,
    etcAbsence,
    sickTardiness,
    justTardiness,
    etcTardiness,
    sickLeave,
    justLeave,
    etcLeave,
    sickNoclass,
    justNoclass,
    etcNoclass,
  };
}

function calculateGPA(lessonList) {
  if (!lessonList || lessonList.length === 0)
    return { gpa: 0, totalCredits: 0, totalGradePoints: 0, gradeAverage: 0 };

  let totalUnits = 0;
  let earnedUnits = 0; // F 제외한 획득 학점
  let passUnits = 0;
  let totalWeightedPoints = 0;
  let totalGradeSum = 0; // 모든 과목 원점수 합계
  let totalGradeCount = 0; // 원점수가 있는 과목 수

  lessonList.forEach((lesson) => {
    // 이수제인 경우 계산에서 제외
    if (lesson.evaluationType === "이수제") {
      const unit =
        lesson.extraAttributes?.creditHour !== undefined &&
        lesson.extraAttributes?.creditHour !== ""
          ? Number(lesson.extraAttributes.creditHour)
          : lesson.lesson?.lesson_hours <= 2
          ? 0.25
          : 0.5;

      if (lesson.grade === "pass") {
        passUnits += unit;
      }

      return; // 이 과목은 건너뛰기
    }

    const subjectSort = lesson.extraAttributes?.subjectSort || "";

    // unit 계산: lesson_hours가 2 이하면 0.25, 3 이상이면 0.5
    const unit =
      lesson.extraAttributes?.creditHour !== undefined &&
      lesson.extraAttributes?.creditHour !== ""
        ? Number(lesson.extraAttributes.creditHour)
        : lesson.lesson?.lesson_hours <= 2
        ? 0.25
        : 0.5;

    // 성적 환산값 결정
    let gradePoint = null;
    const score = parseFloat(lesson.grade);

    const academicStatus = lesson.extra_data?.academicStatus;
    const isValidForGPA =
      !academicStatus ||
      (academicStatus !== "withdrawn" && academicStatus !== "incomplete");

    if (!isNaN(score) && isValidForGPA) {
      // 원점수 평균 계산을 위해 합산
      totalGradeSum += Math.round(score);
      totalGradeCount++;

      // GPA 계산
      if (score >= 92.5) gradePoint = 4;
      else if (score >= 89.5) gradePoint = 3.7;
      else if (score >= 86.5) gradePoint = 3.3;
      else if (score >= 82.5) gradePoint = 3;
      else if (score >= 79.5) gradePoint = 2.7;
      else if (score >= 76.5) gradePoint = 2.3;
      else if (score >= 72.5) gradePoint = 2;
      else if (score >= 69.5) gradePoint = 1.7;
      else if (score >= 66.5) gradePoint = 1.3;
      else if (score >= 59.5) gradePoint = 1;
      else gradePoint = 0;

      if (subjectSort === "HONORS") gradePoint = gradePoint + 0.5;
      else if (subjectSort === "AP") gradePoint = gradePoint + 1;
      else if (subjectSort === "IB") gradePoint = gradePoint + 1;

      if (score < 59.5) gradePoint = 0;

      // 유효한 성적이 있는 경우만 GPA 계산에 포함
      totalUnits += unit;
      totalWeightedPoints += gradePoint * unit;

      // F가 아닌 경우만 earnedUnits에 추가
      if (score >= 59.5) {
        earnedUnits += unit;
      }
    }
  });

  // GPA 계산: (과목별평점 × 과목학점의 합) / (과목학점의 총합)
  const gpa =
    totalUnits > 0 ? (totalWeightedPoints / totalUnits).toFixed(2) : "0.00";

  // 원점수 평균 계산
  const gradeAverage =
    totalGradeCount > 0 ? (totalGradeSum / totalGradeCount).toFixed(1) : "0.0";

  return {
    gpa,
    totalCredits: (earnedUnits + passUnits).toFixed(2),
    totalGradePoints: totalWeightedPoints.toFixed(2),
    gradeAverage,
  };
}

function calculateTotalGPA(
  gradeRows,
  flexibleData = [],
  transferStudentData = [],
  displaySettings = {}
) {
  if (!gradeRows || gradeRows.length === 0) {
    return { totalGPA: 0, totalCredits: 0, totalGradePoints: 0 };
  }

  let totalUnits = 0;
  let earnedUnits = 0; // F 제외한 획득 학점
  let totalWeightedPoints = 0;
  const currentDate = new Date();

  // 학기별 visible/courseTitleOnly 필터링 헬퍼 함수
  const shouldExcludeFromGPA = (studentYear, term) => {
    const semesterKey = `${studentYear}-${term}`;
    const settings = displaySettings[semesterKey] || {
      visible: true,
      courseTitleOnly: false,
    };
    // visible이 false이거나 courseTitleOnly가 true면 GPA 계산에서 제외
    return settings.visible === false || settings.courseTitleOnly === true;
  };

  // 9-12학년 데이터만 필터링하고 현재 날짜 이후 학기는 제외
  const highSchoolData = gradeRows.filter((row) => {
    const grade = parseInt(row.studentYear, 10);
    const termEndDate = row.lesson?.TermDates?.end_date;
    const term = row.lesson?.TermDates?.term_name;

    if (grade < 9 || grade > 12) {
      return false;
    }

    // 현재 날짜와 비교하여 end_date가 현재 날짜보다 이후면 제외
    if (termEndDate) {
      const endDate = new Date(termEndDate);
      if (endDate > currentDate) {
        return false;
      }
    }

    // displaySettings에 따라 제외
    if (shouldExcludeFromGPA(row.studentYear, term)) {
      return false;
    }

    return true;
  });

  // 1. flexibleData 필터링: gradeRows에 존재하는 학년도+학기는 제외
  const filteredFlexibleData = Array.isArray(flexibleData)
    ? flexibleData.filter((flexible) => {
        // flexible 데이터 유효성 검사
        if (!flexible || !flexible.data_value) {
          console.warn("Invalid flexible data:", flexible);
          return false;
        }

        const flexibleStudentYear =
          flexible.data_value?.studentYear?.toString();
        const flexibleTermName = flexible.data_value?.termName?.toString();

        // 필수 데이터가 없으면 제외
        if (!flexibleStudentYear || !flexibleTermName) {
          console.warn(
            "Missing studentYear or termName in flexible data:",
            flexible
          );
          return false;
        }

        // displaySettings에 따라 제외
        if (shouldExcludeFromGPA(flexibleStudentYear, flexibleTermName)) {
          return false;
        }

        // gradeRows에 같은 학년도+학기가 있는지 확인
        const existsInGradeRows = highSchoolData.some((grade) => {
          return (
            grade.studentYear?.toString() === flexibleStudentYear &&
            grade.lesson?.TermDates?.term_name?.toString() === flexibleTermName
          );
        });

        return !existsInGradeRows; // gradeRows에 없는 것만 남김
      })
    : [];

  // 2. transferStudentData 필터링: 필터된 flexibleData의 id를 가진 것만 선별
  const filteredTransferStudentData =
    Array.isArray(transferStudentData) && Array.isArray(filteredFlexibleData)
      ? transferStudentData.filter((transfer) => {
          // transfer 데이터 유효성 검사
          if (!transfer || !transfer.flexible_data_id) {
            console.warn("Invalid transfer student data:", transfer);
            return false;
          }

          return filteredFlexibleData.some(
            (flexible) =>
              flexible?.id?.toString() === transfer.flexible_data_id?.toString()
          );
        })
      : [];

  // convert100 문자 등급을 gradePoint로 변환하는 함수
  const convertGradeToPoint = (convert100) => {
    if (!convert100 || typeof convert100 !== "string") return null;

    const grade = convert100.toUpperCase().trim();

    // GPA 계산에서 제외할 특수 등급들
    const nonGpaGrades = [
      "P",
      "PASS",
      "I",
      "INCOMPLETE",
      "W",
      "WITHDRAWN",
      "AU",
      "AUDIT",
    ];
    if (nonGpaGrades.includes(grade)) {
      return null; // GPA 계산에서 제외
    }

    switch (grade) {
      case "A+":
        return 4;
      case "A":
        return 4;
      case "A-":
        return 3.7;
      case "B+":
        return 3.3;
      case "B":
        return 2.7;
      case "B-":
        return 2.3;
      case "C+":
        return 2;
      case "C":
        return 1.7;
      case "C-":
        return 1.3;
      case "D+":
        return 1;
      case "D":
        return 1;
      case "F":
        return 0;
      default:
        console.warn(
          `Unknown grade: ${convert100}. Excluding from GPA calculation.`
        );
        return null; // 알 수 없는 등급은 GPA 계산에서 제외
    }
  };

  // gradeRows 데이터 처리 (retakeSenior 적용)
  highSchoolData.forEach((lesson) => {
    // 이수제인 경우 계산에서 제외
    if (lesson.evaluationType === "이수제") {
      return;
    }

    // retakeSenior 처리 (gradeTableByTerm과 동일한 로직)
    const retakeSenior = lesson.lesson_management?.retake_senior;
    let finalGrade = lesson.grade;

    if (retakeSenior?.type?.length > 0) {
      finalGrade = retakeSenior.grade; // retakeSenior 점수 사용
    }

    const subjectSort = lesson.extraAttributes?.subjectSort || "";

    // unit 계산
    const unit =
      lesson.extraAttributes?.creditHour !== undefined &&
      lesson.extraAttributes?.creditHour !== ""
        ? Number(lesson.extraAttributes.creditHour)
        : lesson.lesson?.lesson_hours <= 2
        ? 0.25
        : 0.5;

    // 성적 환산값 결정 (retakeSenior 점수 사용)
    let gradePoint = null;
    const score = parseFloat(finalGrade);

    const academicStatus = lesson.extra_data?.academicStatus;
    const isValidForGPA =
      !academicStatus ||
      (academicStatus !== "withdrawn" && academicStatus !== "incomplete");

    if (!isNaN(score) && isValidForGPA) {
      // GPA 계산
      if (score >= 92.5) gradePoint = 4;
      else if (score >= 89.5) gradePoint = 3.7;
      else if (score >= 86.5) gradePoint = 3.3;
      else if (score >= 82.5) gradePoint = 3;
      else if (score >= 79.5) gradePoint = 2.7;
      else if (score >= 76.5) gradePoint = 2.3;
      else if (score >= 72.5) gradePoint = 2;
      else if (score >= 69.5) gradePoint = 1.7;
      else if (score >= 66.5) gradePoint = 1.3;
      else if (score >= 59.5) gradePoint = 1;
      else gradePoint = 0;

      if (subjectSort === "HONORS") gradePoint = gradePoint + 0.5;
      else if (subjectSort === "AP") gradePoint = gradePoint + 1;
      else if (subjectSort === "IB") gradePoint = gradePoint + 1;

      if (score < 59.5) gradePoint = 0;

      // 유효한 성적이 있는 경우만 GPA 계산에 포함
      totalUnits += unit;
      totalWeightedPoints += gradePoint * unit;

      // F가 아닌 경우만 earnedUnits에 추가
      if (score >= 59.5) {
        earnedUnits += unit;
      }
    }
  });

  // 3. transferStudentData 처리 (전학생 데이터)
  filteredTransferStudentData?.forEach((transfer) => {
    // units 유효성 검사
    const unit = Number(transfer.units);
    if (isNaN(unit)) {
      console.warn(`Invalid units for transfer student data:`, transfer);
      return; // 유효하지 않은 units는 건너뛰기
    }

    const gradePoint = convertGradeToPoint(transfer.convert100);

    // gradePoint가 null인 경우 (P, I, W 등) GPA 계산에서 제외
    if (gradePoint === null) {
      return; // GPA 계산에서 제외
    }

    // 유효한 gradePoint인 경우만 GPA 계산에 포함
    totalUnits += unit;
    totalWeightedPoints += gradePoint * unit;

    // F가 아닌 경우만 earnedUnits에 추가
    if (gradePoint > 0) {
      earnedUnits += unit;
    }
  });

  // 총 GPA 계산: (과목별평점 × 과목학점의 합) / (과목학점의 총합)
  const totalGPA =
    totalUnits > 0 ? (totalWeightedPoints / totalUnits).toFixed(2) : "0.00";

  return {
    totalGPA,
    totalCredits: earnedUnits.toFixed(2),
    totalGradePoints: totalWeightedPoints.toFixed(2),
  };
}

function getLessonUnit(lesson) {
  // if (lesson.evaluationType === "이수제") {
  //   return; // 이 과목은 건너뛰기
  // }

  return lesson.extraAttributes?.creditHour !== undefined &&
    lesson.extraAttributes?.creditHour !== ""
    ? Number(lesson.extraAttributes.creditHour)
    : lesson.lesson?.lesson_hours <= 2
    ? 0.25
    : 0.5;
}

function getEffort(lesson) {
  if (
    !lesson?.convert100 ||
    lesson.evaluationType === "이수제" ||
    lesson.extra_data?.academicStatus === "withdrawn" ||
    lesson.extra_data?.academicStatus === "incomplete"
  ) {
    return "-"; // 이수제이거나 W/I인 경우 빈 문자열 반환
  }

  const grade = lesson.convert100?.toUpperCase()?.trim();

  switch (grade) {
    case "A+":
    case "A":
    case "A-":
      return "1";
    case "B+":
    case "B":
    case "B-":
      return "2";
    case "C+":
    case "C":
    case "C-":
      return "3";
    case "D+":
    case "D":
    case "D-":
      return "4";
    case "F":
      return "5";
    default:
      return ""; // 알 수 없는 grade인 경우 빈 문자열 반환
  }
}

// 점수를 문자 등급으로 변환하는 함수 이건 59.5 이상인 경우 반올림 적용 하지 말라고 했던 걸로 기억함
function getLetterGrade(score) {
  if (score >= 97) return "A+";
  if (score >= 93) return "A";
  if (score >= 90) return "A-";
  if (score >= 87) return "B+";
  if (score >= 83) return "B";
  if (score >= 80) return "B-";
  if (score >= 77) return "C+";
  if (score >= 73) return "C";
  if (score >= 70) return "C-";
  if (score >= 67) return "D+";
  if (score >= 60) return "D";
  return "F";
}

function specialGradeTableByTerm(
  term,
  studentYear,
  transferStudentData,
  gradeRows,
  exisitingTransferData,
  schoolYear
) {
  const gradeSystem = exisitingTransferData?.data_value?.gradeSystem || "";

  // AP 과목 존재 여부 확인
  // const apIn11Or12 = gradeRows.some(
  //   (item) =>
  //     item.lesson_management?.ap?.applied === true &&
  //     (item.studentYear === "11" || item.studentYear === "12")
  // );

  // const apIn9Or10 = gradeRows.some(
  //   (item) =>
  //     item.lesson_management?.ap?.applied === true &&
  //     (item.studentYear === "9" || item.studentYear === "10")
  // );

  const retakeSeniorIn11Or12 = gradeRows.some(
    (item) =>
      item.lesson_management?.retake_senior &&
      (item.studentYear === "11" || item.studentYear === "12")
  );

  const retakeSeniorIn9Or10 = gradeRows.some(
    (item) =>
      item.lesson_management?.retake_senior &&
      (item.studentYear === "9" || item.studentYear === "10")
  );

  const selectedTransferStudentData = Array.isArray(transferStudentData)
    ? transferStudentData.filter(
        (r) =>
          r.flexible_data_id.toString() === exisitingTransferData.id.toString()
      )
    : [];

  // 전학 정보 중 해당 학기/학년의 데이터만 필터링
  const filteredData =
    selectedTransferStudentData.length > 0
      ? selectedTransferStudentData.sort((a, b) => {
          // 1. subjectOrder가 있는 것이 우선
          const hasOrderA = a.order ? 1 : 0;
          const hasOrderB = b.order ? 1 : 0;

          if (hasOrderA !== hasOrderB) {
            return hasOrderB - hasOrderA; // subjectOrder가 있는 것이 먼저
          }

          // 2. 둘 다 subjectOrder가 있으면 그 순서대로
          if (hasOrderA && hasOrderB) {
            return Number(a.order) - Number(b.order);
          }

          // 3. 둘 다 subjectOrder가 없으면 과목명으로
          return a.subject_name.localeCompare(b.subject_name);
        })
      : [];

  const generalData = filteredData.filter(
    (item) => item.subject_category !== "arts_pe"
  );
  const artsPeData = filteredData.filter(
    (item) => item.subject_category === "arts_pe"
  );

  // 학기 이름과 학년도 포맷팅
  const formatTermHeader = (term, studentYear, school_Year) => {
    const termName = term === "1" ? "Spring" : "Fall";
    return `(${termName}) School Year ${school_Year} Grade : ${studentYear}th`;
  };

  // earned Credits 계산
  const earnedCredits = filteredData.reduce(
    (acc, item) => acc + Number(item.units),
    0
  );

  return (
    <div className={styles.semesterBlock}>
      <div className={styles.semesterHeader}>
        {formatTermHeader(term, studentYear, schoolYear)}
      </div>
      <table
        style={{
          maxHeight: "250px",
        }}
      >
        <thead>
          <tr>
            <th style={{ width: "38px", padding: "0px" }}>GR</th>
            <th>COURSE TITLE</th>
            <th style={{ width: "40px", padding: "0px" }}>UNITS</th>
            <th style={{ width: "30px", padding: "0px" }}>GD</th>
            {gradeSystem === "korean_public" && (
              <th style={{ width: "50px", padding: "0px" }}>SC/Ave.</th>
            )}
            {(gradeSystem === "other" || gradeSystem === "notAvailable") && (
              <th style={{ width: "50px", padding: "0px" }}>SC</th>
            )}
            {gradeSystem === "korean_public" && (
              <th style={{ width: "56px", padding: "0px", lineHeight: "1" }}>
                Converted
                <br />
                Score
              </th>
            )}
            <th style={{ width: "30px", padding: "0px" }}>EF</th>
          </tr>
        </thead>

        {gradeSystem === "notAvailable" ? (
          <tbody>
            <tr style={{ height: "211px" }}>
              <td
                className="text-center"
                rowSpan={artsPeData && artsPeData.length > 0 ? 2 : 1}
              >
                CORE
              </td>

              <td colSpan="5" className="fw-bold">
                Different semester systems were followed at the school attended
                during{" "}
                {studentYear ? "the " + studentYear + "th grade" : "this grade"}
                . <br />
                As a result, the grades obtained during this period are not
                factored into the overall GPA of this transcript.
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            <tr
              style={{
                overflowY: "auto",
                ...(gradeSystem === "korean_public"
                  ? generalData.length > 9
                    ? { lineHeight: "0.95" }
                    : generalData.length > 7
                    ? { lineHeight: "1.2" }
                    : {}
                  : generalData.length > 15
                  ? { lineHeight: "0.95" }
                  : generalData.length > 12
                  ? { lineHeight: "1.2" }
                  : {}),
                height:
                  gradeSystem === "other"
                    ? "211px"
                    : artsPeData && artsPeData.length > 0
                    ? "124px"
                    : "170px",
              }}
            >
              <td
                className="text-center"
                rowSpan={artsPeData && artsPeData.length > 0 ? 2 : 1}
              >
                CORE
              </td>

              <td className="text-start align-top">
                {generalData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.subject_name}
                    <br />
                  </React.Fragment>
                ))}
              </td>
              <td className="align-top">
                {generalData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.units}
                    <br />
                  </React.Fragment>
                ))}
              </td>
              <td className="align-top">
                {generalData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.convert100}
                    <br />
                  </React.Fragment>
                ))}
              </td>
              <td className="align-top">
                {generalData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.grade}
                    {gradeSystem === "korean_public" && `/${item.average}`}
                    <br />
                  </React.Fragment>
                ))}
              </td>
              {gradeSystem === "korean_public" && (
                <td className="align-top">
                  {generalData.map((item, index) => (
                    <React.Fragment key={index}>
                      {item.converted_score}
                      <br />
                    </React.Fragment>
                  ))}
                </td>
              )}
              <td className="align-top">
                {generalData.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.ef}
                    <br />
                  </React.Fragment>
                ))}
              </td>
            </tr>
            {gradeSystem === "korean_public" &&
              artsPeData &&
              artsPeData.length > 0 && (
                <tr
                  style={{
                    overflowY: "auto",
                    ...(artsPeData.length > 4
                      ? { lineHeight: "0.95" }
                      : artsPeData.length > 2
                      ? { lineHeight: "1.2" }
                      : {}),
                    height: "50px",
                  }}
                >
                  <td className="text-start align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.subject_name}
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.units}
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.convert100}
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        -
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        -
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                  <td className="align-top">
                    {artsPeData.map((item, index) => (
                      <React.Fragment key={index}>
                        {item.ef}
                        <br />
                      </React.Fragment>
                    ))}
                  </td>
                </tr>
              )}

            {gradeSystem === "korean_public" && (
              <tr style={{ height: "37px" }}>
                <td
                  className="text-start p-0 ps-2"
                  colSpan="8"
                  style={{ lineHeight: "1.05" }}
                >
                  Above 1.1 = A (Greater than (average+10%)) / 1.1~0.8 = B
                  (average+10%)~(average-20%)
                  <br />
                  0.8~0.6 = C (average-20%)~(average-40%) / 0.6~0.4 = D
                  (average-40%)~(average-60%)
                  <br />
                  Below 0.4 = F (60% lower than average)
                </td>
              </tr>
            )}
          </tbody>
        )}

        <tfoot>
          <tr>
            <td className="text-start p-0" colSpan="8">
              <span className="ms-5"></span>
              <span className="ms-5"></span>
              <span className="ms-3"></span>
              From {exisitingTransferData?.data_value?.previousSchool ||
                ""}{" "}
              <span className="me-5"></span>
              <span className="me-5"></span>
              Units Earned :{" "}
              {gradeSystem === "notAvailable" ? "" : earnedCredits}
            </td>
          </tr>
          {studentYear === "11" && retakeSeniorIn11Or12 && term === "1" && (
            <tr>
              <td
                colSpan="8"
                className="text-start p-0 ps-2"
                style={{
                  borderLeft: "none",
                  borderRight: "none",
                  borderBottom: "none",
                }}
              >
                Asterisk(*) at the end of course title denotes Retake/Senior
                Packet
              </td>
            </tr>
          )}
          {studentYear === "9" && retakeSeniorIn9Or10 && term === "1" && (
            <tr>
              <td
                colSpan="8"
                className="text-start p-0 ps-2"
                style={{
                  borderLeft: "none",
                  borderRight: "none",
                  borderBottom: "none",
                }}
              >
                Asterisk(*) at the end of course title denotes Retake/Senior
                Packet
              </td>
            </tr>
          )}

          {/* {((studentYear === "10" && apIn9Or10) ||
            (studentYear === "12" && apIn11Or12)) &&
            term === "1" && (
              <tr>
                <td
                  colSpan="8"
                  className="text-start p-0 ps-2"
                  style={{
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "none",
                  }}
                >
                  Asterisk(*) at the end of course title denotes AP
                  courses(provided by SmartSchool or UC Scout)
                </td>
              </tr>
            )} */}
        </tfoot>
      </table>
      {term === "1" && (
        <div>
          <span></span>
        </div>
      )}
    </div>
  );
}
